import React from 'react';
import ProgressBar from '@ui/progress/ProgressBar';
import FormButton from '@ui/button/FormButton';
import Tag from '@ui/tag/Tag';
import Disclosure from '@ui/disclosure/Disclosure';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { BackfillClickRange, Integration } from '@app/model/integration-settings/Integration';
import { useRunIntegrationBackfill } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import {
	backfillButtonLabel,
	backfillRulesText,
	backfillStateLine,
	formatDate,
	formatDayTime,
	isComplete,
	isLocked,
	nextClickLine,
	PROVIDER_LABEL,
	RESULT_TAG,
	STREAM_LABEL,
} from '../IntegrationSettings.utilities';

const range = (value: BackfillClickRange | null) => value === null ? '—' : value.partNumbers.join(', ');

// A click captures unfinished parts within the shared provider allowance. Completed parts stay done.
const ReservationsBackfillTab: React.FunctionComponent<{ integration: Integration }> = ({ integration }) => {
	const toaster = useToaster();
	const run = useRunIntegrationBackfill(error => toaster.error('Backfill', error));
	const backfill = integration.backfill;
	if (backfill === null || integration.provider === null) return <p className="text-sm text-gray-500">Save the settings first.</p>;

	const provider = PROVIDER_LABEL[integration.provider];
	const latestWithDetails = backfill.clicks.find(click => click.technical !== null);
	const canRun = integration.enabled && integration.catalogue.ready && !isLocked(backfill) && !isComplete(backfill) && !run.isPending;
	const nextLine = nextClickLine(backfill);


	return <div>
		<p className="text-sm text-gray-500">
			Window {formatDate(backfill.windowFrom)} – {formatDate(backfill.windowTo)} · {backfill.days.toLocaleString('en-GB')} days.
			{' '}{backfillRulesText(integration.provider, backfill)}
		</p>
		{backfill.streams.map(stream => <div key={stream.kind} className="mt-4">
			<div className="flex items-center justify-between gap-x-3 text-sm">
				<p className="flex items-center gap-x-2 font-medium text-gray-900">
					{STREAM_LABEL[stream.kind]}
				</p>
				<p className="text-gray-500">{stream.doneParts} of {stream.parts.length} parts</p>
			</div>
			<div className="mt-1.5">
				<ProgressBar label={`Backfill of the ${STREAM_LABEL[stream.kind].toLowerCase()}`} value={stream.doneParts} max={stream.parts.length} />
			</div>
		</div>)}
		{nextLine !== null && <p className="mt-3 text-sm text-gray-500">{nextLine}</p>}
		<div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
			<FormButton state="primary" disabled={!canRun} onClick={() => run.mutate(integration.id)}>
				{run.isPending ? 'Importing…' : backfillButtonLabel(backfill)}
			</FormButton>
			<p className="text-sm text-gray-500">
				{integration.enabled ? backfillStateLine(backfill, provider) : 'The integration is off. Switch it on to run the backfill.'}
			</p>
		</div>
		{run.isPending && <p className="mt-2 text-xs text-gray-400">One part can take up to a minute.</p>}
		{backfill.clicks.length > 0 && <div className="mt-6">
			<p className="mb-2 text-sm font-semibold text-gray-900">Past clicks</p>
			<div className="relative overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
				<table className="min-w-full divide-y divide-gray-300">
					<thead>
					<tr>
						<th scope="col" className="py-2.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">Clicked</th>
						{backfill.streams.map(stream => <th key={stream.kind} scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">
							{STREAM_LABEL[stream.kind]}
						</th>)}
						<th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">Result</th>
						<th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">Message</th>
					</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">
					{backfill.clicks.map(click => <tr key={click.clickedAt}>
						<td className="whitespace-nowrap py-2.5 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-6">{formatDayTime(click.clickedAt)}</td>
						{backfill.streams.map(stream => <td key={stream.kind} className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
							{range(stream.kind === 'STAYS' ? click.stays : click.cancellations)}
						</td>)}
						<td className="whitespace-nowrap px-3 py-2.5 text-xs"><Tag state={RESULT_TAG[click.result].state}>{RESULT_TAG[click.result].label}</Tag></td>
						<td className="px-3 py-2.5 text-xs text-gray-900">{click.message}</td>
					</tr>)}
					</tbody>
				</table>
			</div>
			{latestWithDetails !== undefined && latestWithDetails.technical !== null && <div className="mt-3">
				<Disclosure label={`Technical details · click of ${formatDayTime(latestWithDetails.clickedAt)}`}>
					<p className="whitespace-pre-wrap break-all rounded-md bg-gray-50 px-4 py-3 font-mono text-xs text-gray-700">{latestWithDetails.technical.text}</p>
				</Disclosure>
			</div>}
		</div>}
	</div>;
};

export default ReservationsBackfillTab;
